import { useEffect, useState } from 'react';
import { Route, useNavigate, useParams } from 'react-router-dom';
import { Tabs } from '@forgeglobal/design-framework';
import TabContent from '@tcs/common/components/atoms/TabContent';
import AccountsHeader from '@tcs/client-portal-common/components/organisms/Header/IndividualAccountsHeader';
import AccountOverview from '@tcs/client-portal-common/components/templates/AccountOverview';
import AccountActivity from '@tcs/client-portal-individual/pages/Account/AccountActivity';
import AccountDetails from '@tcs/client-portal-individual/pages/Account/AccountDetails';
import AssetValuation from '@tcs/client-portal-individual/pages/Account/AssetValuation';
import useAccounts from '@tcs/client-portal-common/hooks/useAccounts';
import useValuationDetails from '@tcs/client-portal-common/hooks/useValuationDetails';
import { isDeceasedAccount } from '@tcs/client-portal-common/utils/accountUtils';
import { type Account as AccountType } from '@tcs/client-portal-api/types/Account';

interface AccountTabsProps {
  accountNumber: string;
  editEnabled: boolean;
}

const AccountTabs = ({ accountNumber, editEnabled }: AccountTabsProps) => {
  let updatableAssets = [];
  const { loading: loadingValuation, data: valuationData } =
    useValuationDetails(accountNumber);
  if (valuationData) {
    updatableAssets = valuationData.filter((asset) => asset['isUpdateable']);
  }

  return (
    <Tabs>
      <Route
        path="*"
        element={
          <TabContent label="Overview" data-test-id="account-overview-tab">
            <AccountOverview accountNumber={accountNumber} />
          </TabContent>
        }
      />
      <Route
        path="/activity"
        element={
          <TabContent label="Activity" data-test-id="account-activity-tab">
            <AccountActivity accountNumber={accountNumber} />
          </TabContent>
        }
      />
      <Route
        path="/details"
        element={
          <TabContent label="Details" data-test-id="account-details-tab">
            <AccountDetails
              accountNumber={accountNumber}
              editEnabled={editEnabled}
            />
          </TabContent>
        }
      />
      {!loadingValuation && updatableAssets.length > 0 && (
        <Route
          path="/asset_valuation"
          element={
            <TabContent
              label="Asset Valuation"
              data-test-id="accounts-asset-valuation">
              <AssetValuation
                accountNumber={accountNumber}
                valuationData={valuationData}
              />
            </TabContent>
          }
        />
      )}
    </Tabs>
  );
};

const Account = () => {
  const { accountNumber } = useParams();
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string>();
  const [selectedAccount, setSelectedAccount] = useState<AccountType>();
  const { data: accounts, loading } = useAccounts();
  const { loading: loadingValuation } = useValuationDetails(
    selectedAccountNumber,
  );
  const nav = useNavigate();

  // Making sure the user has access to the accountNumber in the url.
  // If not, redirecting to the the users default account page.
  useEffect(() => {
    if (!loading) {
      const account = accounts.find(
        (account) => account.accountNumber === accountNumber,
      );
      if (account) {
        setSelectedAccountNumber(account.accountNumber);
        setSelectedAccount(account);
      } else {
        nav('/accounts');
      }
    }
  }, [accounts, loading, accountNumber, nav]);

  const editEnabled = !isDeceasedAccount(selectedAccount);

  return (
    <AccountsHeader
      accounts={accounts}
      loading={loading || loadingValuation}
      tabs={() =>
        selectedAccountNumber ? (
          <AccountTabs
            accountNumber={selectedAccountNumber}
            editEnabled={editEnabled}
          />
        ) : undefined
      }
    />
  );
};

export default Account;
